import { matchPath, NavigationType } from 'react-router';

import { ROUTES } from '../constants/routes';
import { isAppPath } from './workspaceNavState';

/**
 * Where a back arrow goes: the in-app page this history entry was opened
 * from, however the reader got here (a note attached to a run goes back to
 * the run, not to the note's folder).
 *
 * Recorded per history entry (keyed by `location.key`), from the router's own
 * navigations, so Back and Forward keep it. A change that stays on the same
 * page (a `#run` hash, a canvas view param) is not a page of its own: the
 * entry inherits its predecessor's origin, one step further back, so the
 * arrow skips those and lands on the page itself, as it last looked.
 *
 * A fresh tab or a pasted link has no in-app origin; the arrow then falls
 * back to its own default (the parent folder, the list, the protocol).
 */
export interface BackEntry {
  /** The page's in-app path and query, for the link's `href`. */
  readonly to: string;
  /** The page's pathname, for its name. */
  readonly pathname: string;
  /** How many history entries back it is (`navigate(-steps)`). */
  readonly steps: number;
}

interface LocationLike {
  readonly key: string;
  readonly pathname: string;
  readonly search: string;
}

const STORAGE_KEY = 'bower:back-entries';
/** Entries kept; the oldest go first. A tab's session never needs more. */
const MAX_ENTRIES = 200;

let entries: Map<string, BackEntry> | null = null;

function load(): Map<string, BackEntry> {
  if (entries) return entries;
  entries = new Map();
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) {
      for (const pair of parsed) {
        if (Array.isArray(pair) && typeof pair[0] === 'string' && isBackEntry(pair[1])) {
          entries.set(pair[0], pair[1]);
        }
      }
    }
  } catch {
    // Storage blocked or corrupt: this tab starts without origins.
  }
  return entries;
}

function isBackEntry(value: unknown): value is BackEntry {
  if (typeof value !== 'object' || value === null) return false;
  const { to, pathname, steps } = value as Partial<BackEntry>;
  // Storage outlives deploys and any same-origin script can write it, and `to`
  // becomes a raw `href`: only an in-app path is kept.
  return (
    typeof to === 'string' &&
    isAppPath(to) &&
    typeof pathname === 'string' &&
    typeof steps === 'number' &&
    Number.isInteger(steps) &&
    steps > 0
  );
}

function save(map: Map<string, BackEntry>): void {
  while (map.size > MAX_ENTRIES) {
    const oldest = map.keys().next().value;
    if (oldest === undefined) break;
    map.delete(oldest);
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...map]));
  } catch {
    // Storage blocked or full: origins still hold for this page load.
  }
}

/**
 * Records one router navigation from `from` to `to`. A POP (Back, Forward)
 * revisits an entry that already has its origin, so it records nothing.
 */
export function recordNavigation(
  action: NavigationType,
  from: LocationLike,
  to: LocationLike,
): void {
  if (action === NavigationType.Pop || from.key === to.key) return;
  const map = load();
  const inherited = map.get(from.key);
  let entry: BackEntry | undefined;
  if (action === NavigationType.Replace) {
    // Same slot in history: the replaced entry's origin is still one back.
    entry = inherited;
  } else if (from.pathname === to.pathname) {
    // Same page, new entry: its origin is one step further away.
    entry = inherited && { ...inherited, steps: inherited.steps + 1 };
  } else {
    const back = `${from.pathname}${from.search}`;
    // The same check as a stored entry: `to` becomes a raw `href`, and a
    // pathname can start with `//` (a pasted `app.bowerlabs.ai//host`).
    entry = isAppPath(back) ? { to: back, pathname: from.pathname, steps: 1 } : undefined;
  }
  map.delete(to.key);
  if (entry) map.set(to.key, entry);
  save(map);
}

/** The page this history entry was opened from in the app, or null. */
export function getBackEntry(key: string): BackEntry | null {
  return load().get(key) ?? null;
}

/**
 * Test seam: forget every recorded origin.
 * @internal
 */
export function resetBackEntries(): void {
  entries = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored.
  }
}

/**
 * Records every navigation `router` makes, from its current location on. The
 * app calls this once on its router before `<RouterProvider>` mounts, so each
 * navigation is recorded before the page it lands on renders.
 */
export function trackBackNavigation(router: {
  readonly state: { readonly location: LocationLike };
  subscribe(
    fn: (state: { location: LocationLike; historyAction: NavigationType }) => void,
  ): () => void;
}): () => void {
  let last = router.state.location;
  return router.subscribe(({ location, historyAction }) => {
    // The router also emits on fetchers, blockers and loading states.
    if (location.key === last.key) return;
    recordNavigation(historyAction, last, location);
    last = location;
  });
}

type RouteKey = keyof typeof ROUTES;

/**
 * The page's name as a back arrow says it ("Back to run"). From the route
 * alone, never from the item's title: a label is read by assistive tech and
 * recorded in analytics replay, and the route is enough to say where Back goes.
 *
 * Keyed by every route, so a new route fails the type check until it is
 * named. `null`: never an origin a reader goes back to (a redirect, an auth
 * step, a page that replaces itself), which reads "previous page".
 */
const PAGE_NAMES: Readonly<Record<RouteKey, string | null>> = {
  root: 'Home',
  dashboard: 'Home',
  onboarding: null,
  collections: 'Workspace',
  artifacts: 'Artifacts',
  captureStream: null,
  protocols: 'Protocols',
  equipment: 'Equipment',
  graph: 'Graph',
  trash: 'Trash',
  audit: 'Audit log',
  settings: 'Settings',
  connectorCallback: null,
  account: 'Account',
  admin: 'Admin',
  liveSession: 'live session',
  liveCall: 'call',
  sharedWithMe: 'Shared with me',
  auth: null,
  resetPassword: null,
  authAction: null,
  verifyEmail: null,
  verifyEmailPrompt: null,
  recoverEmail: null,
  authWorkos: null,
  login: null,
  mobileCapture: 'Capture',
  mobileBird: 'Bower',
  mobileWorkspace: 'Workspace',
  mobileLive: null,
  mobileLiveSession: 'live session',
  mobileNotes: null,
  projects: null,
  collection: 'Workspace',
  collectionSingular: null,
  note: 'note',
  protocol: 'protocol',
  protocolRun: 'run',
  spreadsheet: 'spreadsheet',
  link: null,
  attachment: 'attachment',
  equipmentDetail: 'equipment',
  share: 'shared item',
  projectsLegacy: null,
  projectLegacy: null,
  samplesLegacy: null,
  mobileNoteLegacy: null,
  mobileAttachmentLegacy: null,
};

const NAMED_PATTERNS = (Object.keys(PAGE_NAMES) as RouteKey[]).flatMap((key) => {
  const name = PAGE_NAMES[key];
  const route = ROUTES[key];
  return name ? [[typeof route === 'string' ? route : route.pattern, name] as const] : [];
});

export function backPageName(pathname: string): string {
  const hit = NAMED_PATTERNS.find(([pattern]) => matchPath(pattern, pathname));
  return hit ? hit[1] : 'previous page';
}
